import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Save, 
  CheckCircle, 
  AlertTriangle
} from 'lucide-react';

// Demo dataset
const DEMO_TOPICS = [
  {"topic":"Climate change (mitigation)","code":"E1_MIT","x_fin":3.9,"y_imp":4.7,"owner":"ESG","notes":"Scope 2 MB contracts under review"},
  {"topic":"Energy use & efficiency","code":"E1_EN","x_fin":3.6,"y_imp":3.8,"owner":"Operations","notes":"Capex plan FY+1"},
  {"topic":"Air emissions (non-GHG)","code":"E2_AIR","x_fin":2.8,"y_imp":3.4,"owner":"Operations","notes":""},
  {"topic":"Water use & discharge","code":"E3_WAT","x_fin":3.2,"y_imp":3.7,"owner":"Operations","notes":"Two water-stressed sites"},
  {"topic":"Waste & circularity","code":"E5_WAS","x_fin":3.0,"y_imp":3.3,"owner":"Operations","notes":""},
  {"topic":"Biodiversity & land use","code":"E4_BIO","x_fin":2.2,"y_imp":2.9,"owner":"ESG","notes":""},
  {"topic":"Own workforce (H&S)","code":"S1_HS","x_fin":3.5,"y_imp":3.2,"owner":"HR","notes":"TRIR trending down"},
  {"topic":"Diversity & inclusion","code":"S1_DI","x_fin":3.1,"y_imp":3.6,"owner":"HR","notes":"New targets set"},
  {"topic":"Supply chain labor","code":"S2_SC","x_fin":2.9,"y_imp":3.8,"owner":"Procurement","notes":"Audits planned"},
  {"topic":"Business conduct (ABC)","code":"G1_ABC","x_fin":4.2,"y_imp":3.1,"owner":"Legal","notes":"Policy refreshed"},
  {"topic":"Data privacy & security","code":"G1_PRIV","x_fin":3.7,"y_imp":3.4,"owner":"IT/Sec","notes":"ISO 27001 scope"},
  {"topic":"Community impact","code":"S3_COM","x_fin":2.6,"y_imp":3.0,"owner":"ESG","notes":""}
];

const getESRSOptions = (t) => [
  { id: 'ESRS2', name: t('workflow.materialityAnalysis.esrsOptions.ESRS2'), required: true, selected: true },
  { id: 'E1', name: t('workflow.materialityAnalysis.esrsOptions.E1'), selected: false },
  { id: 'E2', name: t('workflow.materialityAnalysis.esrsOptions.E2'), selected: false },
  { id: 'E3', name: t('workflow.materialityAnalysis.esrsOptions.E3'), selected: false },
  { id: 'E4', name: t('workflow.materialityAnalysis.esrsOptions.E4'), selected: false },
  { id: 'E5', name: t('workflow.materialityAnalysis.esrsOptions.E5'), selected: false },
  { id: 'S1', name: t('workflow.materialityAnalysis.esrsOptions.S1'), selected: false },
  { id: 'S2', name: t('workflow.materialityAnalysis.esrsOptions.S2'), selected: false },
  { id: 'S3', name: t('workflow.materialityAnalysis.esrsOptions.S3'), selected: false },
  { id: 'S4', name: t('workflow.materialityAnalysis.esrsOptions.S4'), selected: false },
  { id: 'G1', name: t('workflow.materialityAnalysis.esrsOptions.G1'), selected: false }
];

const MaterialityAnalysisWorkflow = ({ previewOnly = false }) => {
  const { t } = useTranslation();
  
  // State management
  const [stakeholders, setStakeholders] = useState(['Investors', 'Customers', 'Employees', 'Regulators']);
  const [topics, setTopics] = useState(DEMO_TOPICS);
  const [threshold, setThreshold] = useState(3.5);
  const [selectedESRS, setSelectedESRS] = useState(getESRSOptions(t));
  const [rationales] = useState({});
  const [newStakeholder, setNewStakeholder] = useState('');

  // Calculate materiality status
  const getMaterialityStatus = (topic) => {
    const maxScore = Math.max(topic.x_fin, topic.y_imp);
    if (maxScore >= threshold) return 'material';
    if (maxScore >= 3.0) return 'borderline';
    return 'non-material';
  };

  const materialTopics = topics.filter(t => getMaterialityStatus(t) === 'material');

  // Auto-suggest ESRS based on material topics
  const runSuggestions = () => {
    const newSelectedESRS = selectedESRS.map(esrs => {
      if (esrs.required) return esrs;
      
      // Map topic codes to ESRS IDs
      const hasMaterialTopics = materialTopics.some(topic => {
        const topicPrefix = topic.code.split('_')[0]; // E1, S1, G1, etc.
        return esrs.id === topicPrefix;
      });
      
      return { ...esrs, selected: hasMaterialTopics };
    });
    
    setSelectedESRS(newSelectedESRS);
  };



  const handleSave = () => {
    const data = {
      stakeholders,
      topics,
      threshold,
      selectedESRS,
      rationales
    };
    localStorage.setItem('materialityAnalysisData', JSON.stringify(data));
    console.log('Materiality Analysis saved:', data);
  };

  const addStakeholder = () => {
    if (newStakeholder.trim() && !stakeholders.includes(newStakeholder.trim())) {
      setStakeholders([...stakeholders, newStakeholder.trim()]);
      setNewStakeholder('');
    }
  };

  const removeStakeholder = (stakeholder) => {
    setStakeholders(stakeholders.filter(s => s !== stakeholder));
  };

  const updateTopicScore = (topicCode, field, value) => {
    setTopics(topics.map(topic => 
      topic.code === topicCode ? { ...topic, [field]: parseFloat(value) || 0 } : topic
    ));
  };

  const toggleESRS = (esrsId) => {
    setSelectedESRS(selectedESRS.map(esrs => 
      esrs.id === esrsId ? { ...esrs, selected: !esrs.selected } : esrs
    ));
  };

  const clearScores = () => {
    setTopics(topics.map(topic => ({
      ...topic,
      x_fin: 0,
      y_imp: 0
    })));
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6 relative">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('workflow.materialityAnalysis.title')}</h1>
          <p className="text-gray-600 mt-2">{t('workflow.materialityAnalysis.subtitle')}</p>
        </div>
      </div>


      {/* Error/Warning Messages */}

      {topics.some(topic => topic.x_fin <= 0 || topic.y_imp <= 0) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-yellow-800">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-medium">{t('workflow.materialityAnalysis.someTopicsLackScores')}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Stakeholders */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('workflow.materialityAnalysis.stakeholders')}</h3>
            <div className="flex flex-wrap gap-2 mb-4">
              {stakeholders.map((stakeholder, index) => (
                <div key={index} className="flex items-center gap-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                  {stakeholder}
                  <button
                    onClick={() => removeStakeholder(stakeholder)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newStakeholder}
                onChange={(e) => setNewStakeholder(e.target.value)}
                placeholder={t('workflow.materialityAnalysis.addGroup')}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                onKeyPress={(e) => e.key === 'Enter' && addStakeholder()}
              />
              <button
                onClick={addStakeholder}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t('workflow.materialityAnalysis.addGroup')}
              </button>
            </div>
          </div>

          {/* Topics & Scores */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{t('workflow.materialityAnalysis.topicsScores')}</h3>
              <div className="flex gap-2">
                <button className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
                  {t('workflow.materialityAnalysis.importTopics')}
                </button>
                <button 
                  onClick={clearScores}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  {t('workflow.materialityAnalysis.clearScores')}
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 font-medium text-gray-900">Topic</th>
                    <th className="text-center py-2 font-medium text-gray-900">{t('workflow.materialityAnalysis.impactScore')}</th>
                    <th className="text-center py-2 font-medium text-gray-900">{t('workflow.materialityAnalysis.financialScore')}</th>
                    <th className="text-left py-2 font-medium text-gray-900">{t('workflow.materialityAnalysis.owner')}</th>
                    <th className="text-left py-2 font-medium text-gray-900">{t('workflow.materialityAnalysis.notes')}</th>
                  </tr>
                </thead>
                <tbody>
                  {topics.map((topic, index) => (
                    <tr key={index} className="border-b border-gray-100">
                      <td className="py-2 font-medium text-gray-900">{topic.topic}</td>
                      <td className="py-2">
                        <input
                          type="number"
                          min="1"
                          max="5"
                          step="0.1"
                          value={topic.y_imp}
                          onChange={(e) => updateTopicScore(topic.code, 'y_imp', e.target.value)}
                          className="w-16 px-2 py-1 border border-gray-300 rounded text-center"
                        />
                      </td>
                      <td className="py-2">
                        <input
                          type="number"
                          min="1"
                          max="5"
                          step="0.1"
                          value={topic.x_fin}
                          onChange={(e) => updateTopicScore(topic.code, 'x_fin', e.target.value)}
                          className="w-16 px-2 py-1 border border-gray-300 rounded text-center"
                        />
                      </td>
                      <td className="py-2 text-gray-600">{topic.owner}</td>
                      <td className="py-2 text-gray-600">{topic.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Matrix */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{t('workflow.materialityAnalysis.materialityMatrix')}</h3>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">{t('workflow.materialityAnalysis.threshold')}:</label>
                <select
                  value={threshold}
                  onChange={(e) => setThreshold(parseFloat(e.target.value))}
                  className="px-3 py-1 border border-gray-300 rounded text-sm"
                >
                  <option value={3.0}>3.0</option>
                  <option value={3.5}>3.5</option>
                  <option value={4.0}>4.0</option>
                </select>
              </div>
            </div>
            
            {/* Materiality Matrix Visualization */}
            <div className="relative w-full h-96 bg-white border border-gray-300 rounded-lg">
              <div className="absolute inset-0 p-6">
                <div className="w-full h-full relative">
                  {/* Grid lines */}
                  <div className="absolute inset-0">
                    {[0, 1, 2, 3, 4, 5].map(i => (
                      <div key={i}>
                        {/* Horizontal grid lines */}
                        <div 
                          className="absolute w-full border-t border-gray-200" 
                          style={{ top: `${i * 20}%` }} 
                        />
                        {/* Vertical grid lines */}
                        <div 
                          className="absolute h-full border-l border-gray-200" 
                          style={{ left: `${i * 20}%` }} 
                        />
                      </div>
                    ))}
                  </div>
                  
                  
                  {/* Data points */}
                  {topics.map((topic, index) => {
                    const status = getMaterialityStatus(topic);
                    const x = ((topic.x_fin - 1) / 4) * 100;
                    const y = 100 - ((topic.y_imp - 1) / 4) * 100;
                    
                    let color = 'bg-gray-400';
                    if (status === 'material') color = 'bg-red-500';
                    else if (status === 'borderline') color = 'bg-yellow-500';
                    
                    return (
                      <div
                        key={index}
                        className={`absolute w-4 h-4 rounded-full ${color} transform -translate-x-1/2 -translate-y-1/2 cursor-pointer border-2 border-white shadow-md hover:scale-110 transition-transform`}
                        style={{ left: `${x}%`, top: `${y}%` }}
                        title={`${topic.topic}\nFinancial: ${topic.x_fin}\nImpact: ${topic.y_imp}\nStatus: ${status}`}
                      />
                    );
                  })}
                  
                  {/* Threshold lines */}
                  <div 
                    className="absolute w-full border-t-2 border-red-500 opacity-70"
                    style={{ top: `${100 - ((threshold - 1) / 4) * 100}%` }}
                  />
                  <div 
                    className="absolute h-full border-l-2 border-red-500 opacity-70"
                    style={{ left: `${((threshold - 1) / 4) * 100}%` }}
                  />
                  
                  {/* Threshold labels */}
                  <div 
                    className="absolute text-sm text-red-600 font-bold bg-white px-2 py-1 rounded shadow-sm border border-red-200"
                    style={{ 
                      top: `${100 - ((threshold - 1) / 4) * 100}%`, 
                      right: '0.5rem',
                      transform: 'translateY(-50%)' 
                    }}
                  >
                    {threshold}
                  </div>
                  <div 
                    className="absolute text-sm text-red-600 font-bold bg-white px-2 py-1 rounded shadow-sm border border-red-200"
                    style={{ 
                      left: `${((threshold - 1) / 4) * 100}%`, 
                      bottom: '0.5rem',
                      transform: 'translateX(-50%)' 
                    }}
                  >
                    {threshold}
                  </div>
                </div>
              </div>
              
            </div>
            
            {/* Legend */}
            <div className="mt-4 flex justify-center gap-6 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <span>{t('workflow.materialityAnalysis.material')} (≥{threshold})</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <span>{t('workflow.materialityAnalysis.borderline')} (3.0-{threshold-0.1})</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                <span>{t('workflow.materialityAnalysis.nonMaterial')} (&lt;3.0)</span>
              </div>
            </div>
            
            
            <div className="mt-4 flex justify-center">
              <button
                onClick={runSuggestions}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                {t('workflow.materialityAnalysis.useMatrixSuggest')}
              </button>
            </div>
          </div>

          {/* Selected ESRS */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('workflow.materialityAnalysis.selectedESRS')}</h3>
            <div className="space-y-2">
              {selectedESRS.map((esrs) => (
                <label key={esrs.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded">
                  <input
                    type="checkbox"
                    checked={esrs.selected}
                    onChange={() => !esrs.required && toggleESRS(esrs.id)}
                    disabled={esrs.required}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className={`text-sm ${esrs.required ? 'text-gray-500' : 'text-gray-900'}`}>
                    {esrs.name}
                    {esrs.required && ` (${t('workflow.materialityAnalysis.always')})`}
                  </span>
                </label>
              ))}
            </div>
          </div>


        </div>
      </div>

      {/* Action Buttons - Bottom */}
      <div className="flex justify-end gap-4 mt-8 pt-6 border-t border-gray-200">
        <button
          onClick={handleSave}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Save className="w-4 h-4" />
          {t('workflow.materialityAnalysis.save')}
        </button>
        <button
          onClick={runSuggestions}
          className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <CheckCircle className="w-4 h-4" />
          {t('workflow.materialityAnalysis.runSuggestions')}
        </button>
      </div>
      {previewOnly && (
        <div className="absolute inset-0 bg-white/30 backdrop-blur-[1px] z-10 rounded-lg">
        </div>
      )}
    </div>
  );
};

export default MaterialityAnalysisWorkflow;